import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Button, Input, Modal, Tag, Empty, Spin, message, Statistic } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLayerGroup, faPlus, faArrowLeft, faRefresh, faPlay, faEdit, faTrash, faFilter, faUsers, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { fetchSegments, createSegment, updateSegment, deleteSegment } from '../../../Slices/bulkOutreachSlice';
import { useNavigate } from 'react-router-dom';

const glassCard = {
  borderRadius: 16, border: '1px solid rgba(255,255,255,0.18)',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.9) 100%)',
  boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
};

const SegmentManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { segments, segmentsTotal, loading } = useSelector(state => state.bulkOutreach);
  const [search, setSearch] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });

  useEffect(() => { dispatch(fetchSegments({ search })); }, [dispatch, search]);

  const handleCreate = async () => {
    if (!form.name.trim()) return message.warning('Segment name is required');
    const result = await dispatch(createSegment({ name: form.name, description: form.description, leadFilters: {}, contactFilters: {} }));
    if (!result.error) { message.success('Segment created'); setCreateModal(false); setForm({ name: '', description: '' }); }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Delete Segment?', content: 'This cannot be undone.',
      okText: 'Delete', okButtonProps: { danger: true },
      onOk: async () => { await dispatch(deleteSegment(id)); message.success('Segment deleted'); },
    });
  };

  const handleUse = (segment) => {
    navigate('/bulk-outreach/new', { state: { segment } });
  };

  const formatFilterSummary = (seg) => {
    const parts = [];
    const lf = seg.leadFilters || {};
    if (lf.status?.length) parts.push(`Status: ${lf.status.join(', ')}`);
    if (lf.state?.length) parts.push(`State: ${lf.state.join(', ')}`);
    if (lf.channel?.length) parts.push(`Channel: ${lf.channel.join(', ')}`);
    if (lf.priority?.length) parts.push(`Priority: ${lf.priority.join(', ')}`);
    if (lf.assignedTo?.length) parts.push(`Assigned: ${lf.assignedTo.join(', ')}`);
    const cf = seg.contactFilters || {};
    if (cf.title?.length) parts.push(`Title: ${cf.title.join(', ')}`);
    if (cf.optIn?.length) parts.push(`OptIn: ${cf.optIn.join(', ')}`);
    return parts.length > 0 ? parts.join(' | ') : 'No filters set';
  };

  return (
    <div style={{ padding: '24px 32px', minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Button type="text" onClick={() => navigate('/bulk-outreach')} icon={<FontAwesomeIcon icon={faArrowLeft} />} />
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>
            <FontAwesomeIcon icon={faLayerGroup} style={{ marginRight: 10, color: '#8b5cf6' }} />
            Saved Segments
          </h2>
          <Tag style={{ fontSize: 13, padding: '2px 10px', borderRadius: 12, background: '#ede9fe', color: '#8b5cf6', border: 'none' }}>
            {segmentsTotal} segments
          </Tag>
        </div>
        <Button type="primary" icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />}
          onClick={() => setCreateModal(true)}
          style={{ background: '#8b5cf6', borderColor: '#8b5cf6', borderRadius: 8, height: 40 }}>
          New Segment
        </Button>
      </div>

      <Input.Search placeholder="Search segments..." value={search} onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 24, maxWidth: 400, borderRadius: 8 }} allowClear />

      {loading && <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>}

      {!loading && segments.length === 0 && (
        <Empty description="No saved segments. Create segments from the campaign wizard to reuse filters." style={{ marginTop: 60 }} />
      )}

      <Row gutter={[16, 16]}>
        {segments.map(seg => (
          <Col key={seg._id} xs={24} sm={12} lg={8}>
            <Card style={{ ...glassCard, height: '100%' }} bodyStyle={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: '#1a1a2e' }}>{seg.name}</h3>
                {seg.isPublic && <Tag color="#10b981" style={{ fontSize: 11 }}>Public</Tag>}
              </div>

              {seg.description && <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>{seg.description}</p>}

              <div style={{ display: 'flex', gap: 24, marginBottom: 12 }}>
                <Statistic title={<span style={{ fontSize: 11 }}><FontAwesomeIcon icon={faUsers} style={{ marginRight: 4 }} />Leads</span>}
                  value={seg.cachedCount || 0} valueStyle={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e' }} />
                <Statistic title={<span style={{ fontSize: 11 }}><FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 4 }} />Contacts</span>}
                  value={seg.cachedContactCount || 0} valueStyle={{ fontSize: 20, fontWeight: 700, color: '#8b5cf6' }} />
              </div>

              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12, lineHeight: 1.5 }}>
                <FontAwesomeIcon icon={faFilter} style={{ marginRight: 4 }} />
                {formatFilterSummary(seg)}
              </div>

              <div style={{ display: 'flex', gap: 8, fontSize: 11, color: '#9ca3af', marginBottom: 16 }}>
                <span>Used {seg.useCount || 0}x</span>
                {seg.lastUsedAt && <span>| Last: {new Date(seg.lastUsedAt).toLocaleDateString()}</span>}
                <span>| By: {seg.createdByName || 'Unknown'}</span>
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="primary" size="small" onClick={() => handleUse(seg)}
                  style={{ background: '#10b981', borderColor: '#10b981', borderRadius: 6, flex: 1 }}>
                  <FontAwesomeIcon icon={faPlay} style={{ marginRight: 4 }} /> Use
                </Button>
                <Button size="small" onClick={() => navigate(`/bulk-outreach/segments/${seg._id}`)} style={{ borderRadius: 6 }}>
                  <FontAwesomeIcon icon={faEdit} />
                </Button>
                <Button size="small" danger onClick={() => handleDelete(seg._id)} style={{ borderRadius: 6 }}>
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal open={createModal} onCancel={() => setCreateModal(false)} title="Create Segment" okText="Create" onOk={handleCreate}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
          <Input placeholder="Segment name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <Input.TextArea placeholder="Description (optional)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} />
          <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>
            Filters can be configured when using this segment in a campaign wizard.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default SegmentManager;
